import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { MessageCircle, Send, Copy, Users } from "lucide-react";

// Inline WhatsApp logo (Simple Icons path)
const WhatsAppIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    role="img"
    aria-label="WhatsApp"
    {...props}
  >
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.149-.672.149-.198.297-.768.967-.941 1.166-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.148-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.075-.149-.672-1.611-.921-2.206-.242-.579-.487-.5-.672-.51l-.572-.01c-.198 0-.52.074-.793.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.571-.347m-4.421 6.421h-.005a8.658 8.658 0 01-4.688-1.366l-.337-.2-3.48.912.929-3.392-.219-.348a8.682 8.682 0 01-1.337-4.637c.001-4.798 3.908-8.703 8.709-8.703 2.324 0 4.507.905 6.147 2.544a8.561 8.561 0 012.557 6.133c-.003 4.798-3.908 8.703-8.706 8.703M20.52 3.449A11.815 11.815 0 0012.047 0C5.495 0 .273 5.223.27 11.777c0 2.077.543 4.108 1.58 5.9L0 24l6.4-1.679a11.82 11.82 0 005.651 1.439h.005c6.554 0 11.777-5.223 11.78-11.777a11.72 11.72 0 00-3.316-8.534z"
    />
  </svg>
);

interface WhatsAppShareProps {
  property: any;
  phoneNumber?: string;
  variant?: "default" | "outline" | "icon" | "compact" | "dialog";
  size?: "sm" | "default" | "lg";
  className?: string;
  showText?: boolean;
}

export const WhatsAppShare = ({ 
  property, 
  phoneNumber, 
  variant = "default", 
  size = "default",
  className = "",
  showText = true
}: WhatsAppShareProps) => {
  const [open, setOpen] = useState(false);
  const [customPhone, setCustomPhone] = useState(phoneNumber || "");
  const [customMessage, setCustomMessage] = useState("");

  const formatPrice = (price: number | null | undefined) => {
    if (!price || price === 0) return "Price on Request";
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(1)}Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(1)}L`;
    } else {
      return `₹${price.toLocaleString()}`;
    }
  };

  const propertyUrl = `${window.location.origin}/property/${property.slug || property.id}`;
  
  const defaultMessage = `🏠 *${property.title}*

📍 *Location:* ${property.location}, ${property.city}
💰 *Price:* ${formatPrice(property.price)}
🏗️ *Type:* ${property.property_type.replace('_', ' ')}
${property.area_sqft ? `📐 *Area:* ${property.area_sqft} sq.ft` : ''}
${property.bedrooms ? `🛏️ *Bedrooms:* ${property.bedrooms}` : ''}

✨ *Key Features:*
${property.amenities?.slice(0, 3).map((amenity: string) => `• ${amenity}`).join('\n') || '• Premium location\n• Modern amenities\n• Great connectivity'}

🔗 *View Details:* ${propertyUrl}

Interested? Let's discuss! 💬`;

  const handleWhatsAppShare = (phoneNum?: string, message?: string) => {
    const finalMessage = message || customMessage || defaultMessage;
    let whatsappUrl: string;
    
    if (phoneNum || customPhone) {
      const cleanPhone = (phoneNum || customPhone).replace(/[^0-9]/g, '');
      whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(finalMessage)}`;
    } else {
      whatsappUrl = `https://wa.me/?text=${encodeURIComponent(finalMessage)}`;
    }
    
    window.location.href = whatsappUrl;
    
    toast({
      title: "WhatsApp Opened",
      description: "Property details shared via WhatsApp",
    });

    if (variant === "dialog") {
      setOpen(false);
    }
  };

  const copyMessage = async () => {
    const message = customMessage || defaultMessage;
    try {
      await navigator.clipboard.writeText(message);
      toast({
        title: "Message Copied!",
        description: "Property message copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy message",
        variant: "destructive",
      });
    }
  };

  // Dialog variant for advanced sharing
  if (variant === "dialog") {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size={size}
            className={`${className} text-green-600 hover:text-green-700 hover:bg-green-50`}
          >
            <WhatsAppIcon className="h-4 w-4 mr-2" />
            {showText && "Share via WhatsApp"}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsAppIcon className="h-5 w-5 text-green-600" />
              Share Property on WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Send to specific contact (optional)</Label>
              <Input
                id="phone"
                placeholder="+91 9876543210"
                value={customPhone}
                onChange={(e) => setCustomPhone(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to open WhatsApp and choose contact
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message Preview</Label>
              <Textarea
                id="message"
                value={customMessage || defaultMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={12}
                className="text-sm"
              />
            </div>

            <div className="flex space-x-2">
              <Button 
                onClick={() => handleWhatsAppShare()} 
                className="flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                Share via WhatsApp
              </Button>
              <Button variant="outline" onClick={copyMessage}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Icon only variant
  if (variant === "icon") {
    return (
      <Button 
        onClick={() => handleWhatsAppShare(phoneNumber)}
        variant="outline"
        size={size}
        className={`${className} text-green-600 hover:text-green-700 hover:bg-green-50`}
        aria-label="Share on WhatsApp"
      >
        <WhatsAppIcon className="h-4 w-4" />
      </Button>
    );
  }

  // Compact variant
  if (variant === "compact") {
    return (
      <Button 
        onClick={() => handleWhatsAppShare(phoneNumber)}
        variant="outline"
        size={size}
        className={className}
        aria-label="Share on WhatsApp"
      >
        <WhatsAppIcon className="h-4 w-4" />
      </Button>
    );
  }

  // Default button variant
  return (
    <Button 
      onClick={() => handleWhatsAppShare(phoneNumber)}
      variant={variant}
      size={size}
      className={className}
      aria-label="Share on WhatsApp"
    >
      <WhatsAppIcon className="h-4 w-4" />
    </Button>
  );
};